/**
 * Компонент аватара пользователя с автоматической генерацией инициалов и цветного градиента
 * 
 * Использует утилиту getUserInitials для единообразного отображения инициалов:
 * - Если есть displayName "Sasha Loen" → "SL" (первые буквы первых 2х слов)
 * - Если displayName "Sasha" → "S"
 * - Если нет displayName → первая буква username
 * 
 * Использует утилиту getAvatarGradient для генерации уникального градиента на основе userId
 */

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getUserInitials, getAvatarGradient } from '../../lib/utils/avatarUtils';

interface UserAvatarProps {
  /**
   * ID пользователя (для генерации уникального цвета)
   */
  userId: string;
  
  /**
   * URL аватара пользователя
   */
  avatarUrl?: string | null;
  
  /**
   * Отображаемое имя пользователя (приоритет для инициалов)
   */
  displayName?: string | null;
  
  /**
   * Фамилия пользователя (для двухбуквенных инициалов)
   */
  lastName?: string | null;
  
  /**
   * Username пользователя (фолбэк для инициалов)
   */
  username?: string | null;
  
  /**
   * Размер аватара
   * - small: 28px (текст 8px)
   * - compact: 40px (текст 14px)
   * - default: 48px (текст 16px)
   * - medium: 56px (текст 20px)
   * - large: 96px (текст 36px)
   */
  size?: 'small' | 'compact' | 'default' | 'medium' | 'large';
  
  /**
   * Дополнительные CSS классы
   */
  className?: string;
  
  /**
   * Alt текст для изображения
   */
  alt?: string;
  
  /**
   * Вариант отображения (по умолчанию gradient)
   * - gradient: цветной градиент с белым текстом
   * - white: белый фон с темным текстом
   */
  variant?: 'gradient' | 'white';
}

export function UserAvatar({
  userId,
  avatarUrl,
  displayName,
  lastName,
  username,
  size = 'default',
  className,
  alt,
  variant = 'gradient',
}: UserAvatarProps): JSX.Element {
  const initials = getUserInitials({ displayName, lastName, username });
  // Нормализация на входе: null/undefined → undefined для строгих String пропсов Radix
  const altText: string | undefined = alt ?? displayName ?? username ?? undefined;
  const avatarSrc: string | undefined = avatarUrl ?? undefined;
  const gradient = getAvatarGradient(userId);

  // Размер текста в зависимости от размера аватара
  const textSizeClass = {
    small: 'text-[8px]', // 28px аватар
    compact: 'text-sm',   // 40px аватар
    default: 'text-base', // 48px аватар
    medium: 'text-xl',    // 56px аватар
    large: 'text-4xl',    // 96px аватар
  }[size];

  return (
    <Avatar size={size} className={className}>
      {avatarSrc && <AvatarImage src={avatarSrc} alt={altText} />}
      <AvatarFallback 
        style={variant === 'gradient' ? { background: gradient } : undefined}
        className={`font-semibold ${textSizeClass} ${
          variant === 'gradient' 
            ? 'text-white' 
            : 'bg-white text-foreground' // именно белый, а не bg-background — используется поверх цветного фона (Secret Santa)
        }`}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}